/**
 * swatch-cap-guard.js
 * Theme-only enforcement that a customer can order at most MAX free fabric swatches.
 *
 * Layers implemented here:
 *   1. Cart UI lock  — swatch line items show "Free sample" instead of a qty stepper
 *                      (both the cart drawer and the /cart page).
 *   2. AJAX clamp    — intercepts /cart/add + /cart/change + /cart/update so a swatch
 *                      add or quantity bump can't push the swatch total over MAX.
 *   2b. Load trim    — on every cart view, if the swatch total is already over MAX
 *                      (e.g. added via a pasted /cart permalink), it self-heals back to MAX.
 *
 * NOT bulletproof: a determined user hitting express checkout straight from a permalink
 * without ever viewing the cart can still bypass this. That gap needs server-side
 * (a Cart & Checkout Validation Function), which was ruled out on cost.
 *
 * Install: upload as assets/swatch-cap-guard.js and add before </body> in theme.liquid:
 *   {{ 'swatch-cap-guard.js' | asset_url | script_tag }}
 */
(function () {
  'use strict';

  // ---- Config (only thing you'd ever change) --------------------------------
  var SWATCH_HANDLE     = 'fabric-swatch';
  var SWATCH_VARIANT_ID = 47392641712284; // Fabric Swatch › Default Title
  var MAX               = 10;
  var LABEL             = 'Free sample';
  // ---------------------------------------------------------------------------

  var origFetch = window.fetch ? window.fetch.bind(window) : null;

  function getCart() {
    return origFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }
  function isSwatch(item) {
    return !!item && (item.handle === SWATCH_HANDLE || item.variant_id === SWATCH_VARIANT_ID);
  }
  function swatchTotal(cart) {
    return (cart.items || []).reduce(function (n, it) {
      return isSwatch(it) ? n + it.quantity : n;
    }, 0);
  }

  // ---- tiny toast -----------------------------------------------------------
  function toast(msg) {
    var el = document.createElement('div');
    el.className = 'swatch-cap-toast';
    el.textContent = msg;
    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('is-visible'); });
    setTimeout(function () {
      el.classList.remove('is-visible');
      setTimeout(function () { el.remove(); }, 300);
    }, 4000);
  }

  // ---- one-time styles ------------------------------------------------------
  function injectStyles() {
    if (document.getElementById('swatch-cap-styles')) return;
    var s = document.createElement('style');
    s.id = 'swatch-cap-styles';
    s.textContent =
      '.swatch-qty-fixed{display:inline-flex;align-items:center;font-size:13px;font-weight:500;' +
      'color:#6b6b60;white-space:nowrap;}' +
      '.swatch-cap-toast{position:fixed;left:50%;bottom:24px;transform:translateX(-50%) translateY(12px);' +
      'background:#1F2A44;color:#fff;font-size:14px;line-height:1.4;padding:12px 18px;border-radius:8px;' +
      'max-width:min(90vw,420px);box-shadow:0 8px 28px rgba(0,0,0,.22);opacity:0;transition:opacity .25s ease,' +
      'transform .25s ease;z-index:99999;text-align:center;}' +
      '.swatch-cap-toast.is-visible{opacity:1;transform:translateX(-50%) translateY(0);}';
    document.head.appendChild(s);
  }

  // ---- Layer 1: replace the stepper with a fixed label on swatch lines -------
  // The theme puts data-id="{{ item.key }}" on every <quantity-selector>, so we can
  // match line keys returned by /cart.js to the DOM.
  function lockSwatchRows(cart) {
    var keys = {};
    (cart.items || []).forEach(function (it) { if (isSwatch(it)) keys[it.key] = true; });
    document.querySelectorAll('quantity-selector[data-id]').forEach(function (qs) {
      var key = qs.getAttribute('data-id');
      if (!keys[key]) return;
      if (qs.dataset.swatchLocked === '1') return;
      qs.dataset.swatchLocked = '1';
      qs.style.display = 'none';
      var label = document.createElement('span');
      label.className = 'swatch-qty-fixed';
      label.setAttribute('aria-label', 'Quantity 1, free sample');
      label.textContent = LABEL;
      qs.parentNode.insertBefore(label, qs);
    });
  }

  // ---- Layer 2b: heal an over-cap cart on view ------------------------------
  var enforcing = false;
  function enforceCap() {
    if (enforcing || !origFetch) return Promise.resolve();
    enforcing = true;
    return getCart().then(function (cart) {
      var total = swatchTotal(cart);
      if (total <= MAX) { lockSwatchRows(cart); return; }
      var overage = total - MAX;
      var updates = {};
      for (var i = cart.items.length - 1; i >= 0 && overage > 0; i--) {
        var it = cart.items[i];
        if (!isSwatch(it)) continue;
        var take = Math.min(it.quantity, overage);
        updates[it.key] = it.quantity - take;
        overage -= take;
      }
      return origFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function () {
        toast('You can order up to ' + MAX + ' free swatches — we trimmed your cart to ' + MAX + '.');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      });
    }).catch(function () {}).then(function () { enforcing = false; });
  }

  // ---- Layer 2a: intercept AJAX add / change / update -----------------------
  function bodyMentionsSwatch(body) {
    if (!body) return false;
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var id = body.get('id');
        return String(id) === String(SWATCH_VARIANT_ID);
      }
      if (typeof body === 'string') {
        return body.indexOf(String(SWATCH_VARIANT_ID)) !== -1;
      }
      var obj = (typeof body === 'object') ? body : JSON.parse(body);
      return String(obj.id) === String(SWATCH_VARIANT_ID);
    } catch (e) { return false; }
  }
  function requestedQty(body) {
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        return parseInt(body.get('quantity'), 10) || 1;
      }
      if (typeof body === 'string') {
        var m = body.match(/quantity["=:\s]+(\d+)/i);
        return m ? parseInt(m[1], 10) : 1;
      }
      var obj = (typeof body === 'object') ? body : JSON.parse(body);
      return parseInt(obj.quantity, 10) || 1;
    } catch (e) { return 1; }
  }

  if (origFetch) {
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var opts = init || (typeof input === 'object' ? input : {});
      var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);

      if (isAdd && opts && opts.body && bodyMentionsSwatch(opts.body)) {
        var want = requestedQty(opts.body);
        return getCart().then(function (cart) {
          var have = swatchTotal(cart);
          if (have + want > MAX) {
            toast('You can order up to ' + MAX + ' free swatches. You already have ' + have + '.');
            // Resolve with a benign error so the theme doesn't hard-crash.
            return new Response(
              JSON.stringify({ status: 422, message: 'Swatch limit', description: 'Max ' + MAX + ' swatches.' }),
              { status: 422, headers: { 'Content-Type': 'application/json' } }
            );
          }
          return origFetch(input, init);
        });
      }
      return origFetch(input, init);
    };
  }

  // ---- run on load + whenever the cart re-renders ---------------------------
  function boot() {
    injectStyles();
    enforceCap();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme fires these after cart drawer / page updates.
  document.addEventListener('line-item:change:end', function () { enforceCap(); });
  document.addEventListener('cart:refresh', function () { setTimeout(enforceCap, 150); });

  // Fallback: re-lock rows if the cart HTML is swapped without firing an event.
  var mo = new MutationObserver(function () {
    if (document.querySelector('quantity-selector[data-id]:not([data-swatch-locked])')) {
      getCart().then(lockSwatchRows).catch(function () {});
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();
